"use client";

import { parseScopedThreadKey } from "@amoreg/client-runtime/environment";
import { useEffect } from "react";

import { usePreviewMiniPlayerStore } from "~/previewMiniPlayerStore";
import { isPreviewSupportedInRuntime } from "~/previewStateStore";
import { useRightPanelStore } from "~/rightPanelStore";
import { sendBrowserChromeAction } from "~/state/browserPreview";

import {
  KEEP_AWAKE_BEAT_MS,
  LIVE_VIEW_FRAME_ATTRIBUTE,
  browserEngagement,
  browsersToKeepAwake,
} from "./CloudBrowserKeepAwake.logic";
import { liveViewInUse } from "./CloudBrowserView.logic";

/**
 * Keeps the cloud browser on screen awake while the person uses it
 * (CloudBrowserKeepAwake.logic); chats in the background let theirs sleep. The provider's live
 * view bypasses the server's broker, so its clicks never reach the idle
 * clock; the browser views report use (browserEngagement) and this reports
 * it to the server with `touch`, which only refreshes a running browser's
 * clock: it never wakes or starts one. One loop for the page, mounted once.
 */
export function CloudBrowserKeepAwake() {
  useEffect(() => {
    // The desktop preview runs no cloud browser.
    if (isPreviewSupportedInRuntime()) return;
    const beat = () => {
      const focused = document.activeElement;
      const inUse = liveViewInUse({
        visible: document.visibilityState === "visible",
        focusedInView:
          document.hasFocus() && focused instanceof HTMLIFrameElement && focused.hasAttribute(LIVE_VIEW_FRAME_ATTRIBUTE),
        lastSignAt: browserEngagement.lastSignAt,
        now: Date.now(),
      });
      if (!inUse) return;
      const threadKeys = browsersToKeepAwake({
        panels: useRightPanelStore.getState().byThreadKey,
        miniPlayers: usePreviewMiniPlayerStore.getState().byThreadKey,
        onScreen: (threadKey) => browserEngagement.onScreen(threadKey),
      });
      for (const threadKey of threadKeys) {
        const ref = parseScopedThreadKey(threadKey);
        if (ref) void sendBrowserChromeAction(ref.threadId, "touch").catch(() => {});
      }
    };
    const timer = window.setInterval(beat, KEEP_AWAKE_BEAT_MS);
    return () => window.clearInterval(timer);
  }, []);
  return null;
}
